<template lang="pug">
    v-app
        VolieMainTopNav(v-if="!renderBigBoard")

        //- Render component from shared link if valid
        #shared-link-app-body(v-if="validSharedLink" :style="renderBigBoard ? '' : 'margin-top: 64px;'")
            PasswordForm(
                v-if="requiresPassword && !validatedPassword"
                :shared-link="sharedLink"
                :validate-password-success="validatePasswordSuccess"
            )
            template(v-else)
                BigBoard(
                    v-if="renderBigBoard"
                    :account-id="sharedLink.account_id || sharedLink.shareable_id"
                    :shared-link-mode="true"
                )
                LiveDashboard(
                    v-if="renderLiveDashboard"
                    :account-id="sharedLink.account_id || sharedLink.shareable_id"
                    :shared-link-mode="true"
                )
                CustomerProfile(
                    v-if="renderCustomerProfile"
                    :customer-id="sharedLink.shareable_id"
                    :hide-create-update-destroy-buttons="true"
                    source="shared_link"
                )
                ConferenceShowFull(
                    v-if="renderConferenceShowFull"
                    :conference-vid="sharedLink.shareable_vid"
                    :hide-create-update-destroy-buttons="true"
                    :hide-download-buttons="hideDownloadButtons"
                )
                EmailShow(
                    v-if="renderEmailShow"
                    :email-key="sharedLink.shareable_vid"
                    :hide-download-buttons="hideDownloadButtons"
                )
                TicketShow(
                    v-if="renderTicketShow"
                    :ticket-key="sharedLink.shareable_vid"
                    :hide-download-buttons="hideDownloadButtons"
                    source="shared_link"
                )
                ScorecardShow(
                    v-if="renderScorecardShow"
                    :hide-download-buttons="hideDownloadButtons"
                    :scorecard="sharedLinkObject"
                    :scorecard-id="sharedLink.shareable_id"
                    :set-shared-link-object="setSharedLinkObject"
                    source="shared_link"
                )
                LlmAssessmentShow(
                    v-if="renderLlmAssessmentShow"
                    :llm-assessment-vid="sharedLink.shareable_vid"
                    :hide-download-buttons="hideDownloadButtons"
                    source="shared_link"
                )

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';
    import { SharedLinkHeartBeat, SharedLinkHeartBeatDeactivate } from '@/data';

    // Components
    import ConferenceShowFull from '@/components/conferences/show.vue';
    import CustomerProfile from '@/components/customers/base_customer_profile.vue';
    import EmailShow from "@/components/emails/show.vue";
    import LlmAssessmentShow from '@/components/llm_assessments/show.vue';
    import PasswordForm from '@/components/shared_links/password_form.vue';
    import ScorecardShow from "@/components/scorecards/show.vue";
    import TicketShow from "@/components/tickets/show.vue";
    import VolieMainTopNav from '@/components/shared/volie_main_top_nav.vue';
    import BigBoard from '@/views/dashboards/big_board.vue';
    import LiveDashboard from '@/views/dashboards/live_dashboard.vue';

    export default Vue.extend({
        name: 'App',
        data() {
            return {
                validatedPassword: false,
                heartbeatInterval: null as any,
                sharedLinkObject: null,
            }
        },
        methods: {
            validatePasswordSuccess() {
                this.validatedPassword = true;
            },
            sendHeartbeat() {
                if (!this.shouldSendHeartbeat) {
                    return;
                }
                SharedLinkHeartBeat({
                    shared_link_id: this.sharedLink?.id,
                }).catch((err) => {
                    console.error('Failed to send heartbeat:', err);
                });
            },
            setSharedLinkObject(obj) {
                this.sharedLinkObject = obj;
            },
            startHeartbeat() {
                if (!this.shouldSendHeartbeat) {
                    return;
                }
                // Send initial heartbeat
                this.sendHeartbeat();
                
                // Then send heartbeat every 5 minutes (300000 ms)
                // there is a clock job (FlatlineInactiveHeartBeats)
                // that will deactivate the heartbeat if the user is not active for 5 minutes
                this.heartbeatInterval = setInterval(() => {
                    this.sendHeartbeat();
                }, 300000);
            },
            stopHeartbeat() {
                if (this.heartbeatInterval) {
                    clearInterval(this.heartbeatInterval);
                    this.heartbeatInterval = null;
                }
                if (this.sharedLink?.id > 0) {
                    SharedLinkHeartBeatDeactivate({
                        shared_link_id: this.sharedLink?.id,
                    }).catch((err) => {
                        console.error('Failed to deactivate heartbeat:', err);
                    });
                }
            },
        },
        computed: {
            renderConferenceShowFull() {
                return this.sharedLink?.component === "conference_show_full" && this.sharedLink?.shareable_vid?.length > 0 && this.sharedLink?.shareable_type === 'conference';
            },
            renderCustomerProfile() {
                return this.sharedLink?.component === "customer_profile" && this.sharedLink?.shareable_id > 0 && this.sharedLink?.shareable_type === 'customer';
            },
            renderBigBoard() {
                return this.sharedLink?.component === "big_board" && (this.sharedLink?.shareable_id > 0 || this.sharedLink?.account_id > 0) && this.sharedLink?.shareable_type === 'account';
            },
            renderLiveDashboard() {
                return this.sharedLink?.component === "live_dashboard" && (this.sharedLink?.shareable_id > 0 || this.sharedLink?.account_id > 0) && this.sharedLink?.shareable_type === 'account';
            },
            renderEmailShow() {
                return this.sharedLink?.component === "email_show" && this.sharedLink?.shareable_id > 0 && this.sharedLink?.shareable_type === 'email';
            },
            renderLlmAssessmentShow() {
                return this.sharedLink?.component === "pulse_assessment_show" && this.sharedLink?.shareable_id > 0 && this.sharedLink?.shareable_type === 'pulse_assessment';
            },
            renderScorecardShow() {
                return this.sharedLink?.component === "scorecard_show" && this.sharedLink?.shareable_id > 0 && this.sharedLink?.shareable_type === 'scorecard';
            },
            renderTicketShow() {
                return this.sharedLink?.component === "ticket_show" && this.sharedLink?.shareable_id > 0 && this.sharedLink?.shareable_type === 'ticket';
            },
            requiresPassword() {
                return this.sharedLink?.access === "password";
            },
            sharedLink() {
                return VolieState.sharedLink();
            },
            hideDownloadButtons() {
                return this.sharedLink?.downloads !== "enabled" || true;
            },
            validSharedLink() {
                return this.sharedLink?.id > 0;
            },
            shouldSendHeartbeat() {
                return (this.renderBigBoard || this.renderLiveDashboard) && this.validSharedLink && !this.requiresPassword;
            },
        },
        components: {
            ConferenceShowFull,
            CustomerProfile,
            BigBoard,
            LiveDashboard,
            EmailShow,
            LlmAssessmentShow,
            PasswordForm,
            ScorecardShow,
            TicketShow,
            VolieMainTopNav,
        },
        beforeDestroy() {
            this.stopHeartbeat();
        },
        destroyed() {
        },
        created() {
        },
        mounted() {
            if (this.shouldSendHeartbeat && (!this.requiresPassword || this.validatedPassword)) {
                this.startHeartbeat();
            }
        },
        watch: {
            validatedPassword(newVal) {
                // Start heartbeat once password is validated
                if (newVal && this.shouldSendHeartbeat) {
                    this.startHeartbeat();
                }
            },
        },
    });
</script>

<style lang="scss">
    @import '../styles/styles.scss';

    #shared-link-app-body {
        margin-top: 64px;
    }
</style>
